export interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  coverImage: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  featured: boolean;
}

export const articles: Article[] = [
  {
    id: '1',
    slug: 'composition-rule-of-thirds',
    title: '手机自拍构图入门：三分法则的巧妙运用',
    excerpt: '三分法则是摄影构图中最基础也最实用的技巧之一。本文将详细讲解如何在手机自拍中运用三分法则，让你的照片瞬间提升专业感。',
    content: `<p>三分法则是摄影中最经典、最基础的构图法则，也是每位摄影爱好者必须掌握的第一课。简单来说，三分法则就是将画面用两条横线和两条竖线均匀分成九个相等的格子，然后将拍摄主体放置在这些线条的交点或线条上。</p>
<h2>为什么三分法则有效</h2>
<p>人的视觉系统天然会被偏离中心的位置所吸引。将主体放在画面正中央虽然对称，但往往缺乏动感和张力。而三分法则能够创造出更自然、更舒适的视觉平衡。</p>
<h2>手机自拍中的三分法则应用</h2>
<p>在手机自拍时，你可以打开相机设置中的"网格线"功能，这样屏幕上就会出现九宫格辅助线。将自己的眼睛或面部放置在画面中上方的两个交叉点之一，这样拍出来的自拍会比居中构图更有层次感。</p>
<h3>实用技巧一：眼神方向留空间</h3>
<p>如果你的眼神看向某个方向，记得在那个方向多留一些空间，这样画面会更有呼吸感。例如，如果你面向右侧，就把自己放在左侧的三分线上。</p>
<h3>实用技巧二：配合背景元素</h3>
<p>利用背景中的线条、建筑或自然元素来强化三分构图。比如将地平线放在上三分之一或下三分之一处，而不是正中央。</p>
<h2>常见错误与改进</h2>
<p>很多新手在自拍时容易犯的错误是：面部居中、头顶留白过多、忽略了背景元素的引导作用。记住，三分法则是一个指导原则而非绝对规则，当你熟练掌握后，完全可以灵活变通。</p>
<h2>总结</h2>
<p>三分法则是提升自拍质量的捷径，建议每次拍照前都打开网格线，刻意练习一周，你会发现自己的自拍水平有明显的进步。下一篇文章我们将介绍更高级的引导线构图技巧。</p>`,
    category: 'composition',
    coverImage: 'https://readdy.ai/api/search-image?query=Aesthetic%20selfie%20photography%20with%20rule%20of%20thirds%20grid%20overlay%20demonstration%2C%20portrait%20of%20a%20person%20positioned%20on%20the%20left%20third%20line%2C%20warm%20natural%20lighting%2C%20cream%20minimalist%20background%2C%20clean%20modern%20composition%2C%20professional%20tutorial%20style&width=800&height=500&seq=article-comp-01&orientation=landscape',
    author: '光影捕手',
    date: '2026-07-28',
    readTime: '8分钟',
    tags: ['三分法则', '构图入门', '手机自拍', '基础教程'],
    featured: true,
  },
  {
    id: '2',
    slug: 'composition-leading-lines',
    title: '高级自拍构图技巧：引导线与框架构图',
    excerpt: '超越了基础三分法后，引导线和框架构图是让你的自拍作品脱颖而出的关键。学习如何利用环境元素引导观众视线。',
    content: `<p>当你已经熟练掌握三分法则之后，是时候进阶到更高级的构图技巧了。引导线和框架构图是摄影大师们最常用的两种手法，它们能让你的自拍作品拥有电影般的质感和深度。</p>
<h2>什么是引导线构图</h2>
<p>引导线是指画面中能够引导观众视线向主体移动的线条元素。这些线条可以是实际的线条，如道路、栏杆、建筑边缘，也可以是虚拟的线条，如目光方向、手势指向等。</p>
<h2>自拍中寻找引导线</h2>
<p>在日常生活中，引导线无处不在：楼梯的扶手、长廊的柱子、街道的延伸线、树木的排列等等。自拍时，将这些线条放在画面中，让它们指向或汇聚到你的面部，就能创造出强烈的视觉焦点。</p>
<h3>常见引导线场景</h3>
<p>1. 长廊或走廊：站在长廊中轴线，利用透视汇聚线将视线引向自己。</p>
<p>2. 楼梯：从楼梯上方或下方拍摄，阶梯形成自然的引导线。</p>
<p>3. 护栏或围栏：侧身依靠护栏，让线条延伸至远方。</p>
<p>4. 道路：站在道路一侧，利用路缘线或车道线做引导。</p>
<h2>框架构图的魅力</h2>
<p>框架构图是利用前景元素形成一个"画框"，将被摄主体框在其中。这种手法能够增加画面的层次感和窥视感，让观众仿佛透过一扇窗户在观看。</p>
<h3>天然框架元素</h3>
<p>门窗、拱门、树枝形成的空隙、镜子、甚至手机的屏幕反射都可以作为框架。善用这些元素，你的自拍会立刻变得与众不同。</p>
<h2>实践建议</h2>
<p>下次出门时，试着用摄影师的眼光观察周围环境，留意那些自然的线条和框架。拍自拍时多走几步、换个角度，你会发现同一个场景能拍出截然不同的作品。</p>`,
    category: 'composition',
    coverImage: 'https://readdy.ai/api/search-image?query=Portrait%20photography%20using%20leading%20lines%20of%20a%20long%20corridor%20with%20arched%20columns%20converging%20toward%20the%20subject%2C%20warm%20afternoon%20light%2C%20elegant%20architecture%2C%20cream%20and%20beige%20tones%2C%20professional%20composition%20demonstration&width=800&height=500&seq=article-comp-02&orientation=landscape',
    author: '构图大师',
    date: '2026-07-25',
    readTime: '10分钟',
    tags: ['引导线', '框架构图', '进阶技巧', '手机自拍'],
    featured: true,
  },
  {
    id: '3',
    slug: 'composition-symmetry',
    title: '对称构图在自拍中的艺术表现',
    excerpt: '对称是最古老也最令人愉悦的构图方式之一。掌握对称自拍的技巧，创造出令人惊叹的视觉平衡感。',
    content: `<p>对称是人类审美中最原始也最强烈的偏好之一。从古建筑到现代设计，对称无处不在。在自拍摄影中，巧妙运用对称构图能够创造出极具冲击力和艺术感的作品。</p>
<h2>对称自拍的类型</h2>
<p>1. 镜面反射对称：利用镜子、水面、玻璃等反射面来创造完美的对称效果。</p>
<p>2. 建筑对称：站在对称建筑的中间位置，让建筑本身的对称结构衬托自拍主体。</p>
<p>3. 自然对称：利用倒影、隧道、桥梁等自然或人造场景的对称元素。</p>
<h2>镜面自拍的高级玩法</h2>
<p>镜子自拍不仅仅是站在镜子前按快门。尝试将手机也纳入构图，让画面中出现"无限反射"的效果。或者利用两面镜子形成多角度反射，创造超现实的自拍效果。</p>
<h2>不完全对称的美</h2>
<p>完美的对称有时会显得过于死板。在对称的大框架下加入微小的不对称元素——比如稍微歪一下头、让一边的头发垂下——反而能让画面更有生命力。这就是"破"的艺术。</p>
<h2>后期处理的对称增强</h2>
<p>利用手机修图软件中的镜像或对称工具，可以将一张普通的自拍变成令人惊叹的对称艺术作品。但要注意适度，过度的对称处理会显得不自然。</p>`,
    category: 'composition',
    coverImage: 'https://readdy.ai/api/search-image?query=Artistic%20symmetrical%20selfie%20photography%20using%20a%20large%20ornate%20mirror%20in%20a%20bright%20minimalist%20room%2C%20perfectly%20balanced%20composition%2C%20warm%20natural%20lighting%2C%20cream%20walls%2C%20elegant%20and%20sophisticated%20aesthetic&width=800&height=500&seq=article-comp-03&orientation=landscape',
    author: '光影捕手',
    date: '2026-07-20',
    readTime: '7分钟',
    tags: ['对称构图', '镜面自拍', '艺术摄影', '创意技巧'],
    featured: false,
  },
  {
    id: '4',
    slug: 'lighting-golden-hour',
    title: '自然光自拍完全指南：黄金时刻的魅力',
    excerpt: '黄金时刻——日出后和日落前的一小时，是摄影师最钟爱的拍摄时间。掌握黄金时刻的自拍技巧，让每一张照片都沐浴在温暖的光芒中。',
    content: `<p>每个摄影师都知道，光线是摄影的灵魂。而一天中最美的光线，无疑来自黄金时刻——日出后和日落前大约一小时的这段时间。此时太阳处于低角度，光线经过厚厚的大气层散射，变得柔和而温暖。</p>
<h2>黄金时刻光线的特点</h2>
<p>1. 色温偏暖：光线呈现出美丽的金色或橙色，自带滤镜效果。</p>
<p>2. 光线柔和：低角度的阳光经过大气散射，阴影变得柔软，不会产生刺眼的高光。</p>
<p>3. 方向性强：光线有明显的方向性，能创造出立体感和轮廓光。</p>
<h2>黄金时刻自拍技巧</h2>
<p>面朝光源方向，让温暖的阳光均匀地洒在脸上，这时的光线自带柔焦效果。侧光拍摄则能创造出更立体的面部轮廓，一侧明亮一侧柔和，非常有质感。</p>
<h3>逆光的魔力</h3>
<p>让阳光从身后照射过来，头发和肩膀边缘会形成美丽的金色轮廓光。这时可以稍微提高曝光补偿，让面部不至于太暗。或者使用手机的HDR模式来平衡光比。</p>
<h2>不同季节的黄金时刻</h2>
<p>夏季的黄金时刻光线最强烈，色彩最浓郁；冬季的光线更柔和，持续时间也略长。了解不同季节的光线特点，能帮助你更好地规划拍摄计划。</p>`,
    category: 'lighting',
    coverImage: 'https://readdy.ai/api/search-image?query=Beautiful%20golden%20hour%20portrait%20with%20warm%20sunset%20backlight%20creating%20a%20glowing%20hair%20rim%20light%20effect%2C%20soft%20dreamy%20atmosphere%2C%20outdoor%20natural%20setting%2C%20cream%20and%20gold%20color%20palette%2C%20professional%20photography&width=800&height=500&seq=article-light-01&orientation=landscape',
    author: '光线魔术师',
    date: '2026-07-18',
    readTime: '9分钟',
    tags: ['黄金时刻', '自然光', '逆光拍摄', '光线技巧'],
    featured: true,
  },
  {
    id: '5',
    slug: 'lighting-indoor',
    title: '室内自拍光线布置技巧：从零打造专业光效',
    excerpt: '不用昂贵的摄影灯，利用家中现有条件就能创造出专业级的室内自拍光线。从窗户光到台灯布光，全面解析。',
    content: `<p>很多摄影爱好者认为，室内自拍的光线条件不如室外，其实这是一种误解。只要掌握了正确的布光技巧，家里的任何一个角落都可以成为你的私人影棚。</p>
<h2>窗户光——最好的免费光源</h2>
<p>大窗户提供的柔和散射光是最理想的室内光源。将拍摄位置放在窗户侧面大约45度的位置，光线会在面部形成自然的明暗过渡。避免正对窗户，那样光线太平；也避免完全背对窗户，面部会过暗。</p>
<h2>利用反光板（或替代品）</h2>
<p>不需要购买专业反光板，一面白色泡沫板、一张大白纸、甚至一件白色T恤都可以充当反光板。将其放在阴影一侧，可以填充暗部，减少明暗反差。</p>
<h3>DIY补光方案</h3>
<p>1. 白色泡沫板：廉价且效果出色，放在暗部一侧即可。</p>
<p>2. 铝箔纸：揉皱后贴在纸板上，可以制造更强的反射光。</p>
<p>3. 手机屏幕：在极暗环境中，打开白色页面充当微弱补光。</p>
<h2>台灯布光法</h2>
<p>一盏普通的台灯也可以成为很好的人造光源。将台灯放在侧前方45度、略高于眼睛的位置，就能模拟出类似影棚的蝴蝶光效果。如果光线太硬，可以在灯前蒙一层薄纸巾来柔化光线。</p>
<h2>色温注意事项</h2>
<p>注意不同光源的色温差异。窗户光是偏冷的（约5500K），而普通灯泡是偏暖的（约2800K）。混用不同色温的光源会让画面出现奇怪的颜色，尽量统一使用同一种光源。</p>`,
    category: 'lighting',
    coverImage: 'https://readdy.ai/api/search-image?query=Cozy%20indoor%20photography%20setup%20with%20window%20light%20and%20a%20simple%20reflector%20board%2C%20minimalist%20room%20with%20cream%20walls%2C%20warm%20ambient%20lighting%2C%20DIY%20photography%20studio%20concept%2C%20soft%20and%20inviting%20atmosphere&width=800&height=500&seq=article-light-02&orientation=landscape',
    author: '光线魔术师',
    date: '2026-07-15',
    readTime: '11分钟',
    tags: ['室内布光', '窗户光', 'DIY补光', '自拍技巧'],
    featured: false,
  },
  {
    id: '6',
    slug: 'lighting-backlight',
    title: '逆光自拍：创造梦幻光影效果的终极指南',
    excerpt: '逆光拍摄是许多摄影师的终极追求。学会掌控逆光，创造出发丝光、剪影和梦幻光斑效果。',
    content: `<p>逆光拍摄可能是所有光线类型中最难掌握但也最具魅力的。当光源位于被摄主体后方时，你的自拍可以呈现出发丝光、柔焦光晕、甚至戏剧性的剪影效果。</p>
<h2>逆光自拍的三种经典效果</h2>
<p>1. 发丝光效果：让阳光从脑后穿过头发，发丝边缘会出现一圈金色光晕，非常梦幻。</p>
<p>2. 剪影效果：在强烈背光下故意让面部曝光不足，只保留轮廓线条，极具艺术感。</p>
<p>3. 柔焦光晕：利用逆光产生镜头光晕，营造朦胧浪漫的氛围。</p>
<h2>控制曝光的技巧</h2>
<p>逆光最大的挑战是曝光控制。手机的自动曝光通常会让主体变得很暗。解决方法：</p>
<p>1. 点击屏幕上你的面部位置进行测光和对焦。</p>
<p>2. 向上滑动曝光滑块增加曝光补偿。</p>
<p>3. 开启HDR模式来保留更多高光和阴影细节。</p>
<h2>最佳拍摄时间</h2>
<p>低角度的太阳（日出后和日落前一小时）是逆光自拍的最佳时机。此时光线角度低，强度适中，可以轻松获得美丽的逆光效果而不会造成过强的眩光。</p>`,
    category: 'lighting',
    coverImage: 'https://readdy.ai/api/search-image?query=Dreamy%20backlit%20portrait%20with%20soft%20lens%20flare%20and%20golden%20rim%20light%20around%20hair%2C%20ethereal%20atmosphere%2C%20warm%20sunset%20glow%2C%20outdoor%20nature%20setting%2C%20cream%20and%20gold%20tones%2C%20professional%20backlight%20photography&width=800&height=500&seq=article-light-03&orientation=landscape',
    author: '光线魔术师',
    date: '2026-07-12',
    readTime: '8分钟',
    tags: ['逆光拍摄', '发丝光', '剪影', '光影效果'],
    featured: true,
  },
  {
    id: '7',
    slug: 'editing-lightroom-mobile',
    title: 'Lightroom手机版修图入门：从零到精通的完整教程',
    excerpt: 'Adobe Lightroom手机版是目前最强大的移动修图工具之一。本教程将带领摄影新手一步步掌握Lightroom的核心功能。',
    content: `<p>Adobe Lightroom手机版（免费版）已经足够强大，能够满足绝大多数自拍爱好者的修图需求。本文将从零开始，带你全面了解Lightroom的各项核心功能。</p>
<h2>基础面板解析</h2>
<p>打开一张照片后，你会看到底部的调整面板。最核心的几个滑块：</p>
<p>1. 曝光度：控制照片整体亮度，是最常用的调整。</p>
<p>2. 对比度：增加或减少明暗反差。</p>
<p>3. 高光与阴影：分别控制亮部和暗部的细节。</p>
<p>4. 白色色阶与黑色色阶：调整最亮和最暗的部分。</p>
<h2>自拍人像专属调整</h2>
<p>对于自拍人像，有几个特别实用的功能：</p>
<p>1. 纹理与清晰度：适度增加可以让皮肤和发丝更有质感，但不要过度。</p>
<p>2. 去朦胧：去除雾蒙蒙的感觉，让照片更通透。</p>
<p>3. 鲜艳度与饱和度：鲜艳度比饱和度更自然，优先使用。</p>
<h2>蒙版工具——局部调整的利器</h2>
<p>Lightroom的蒙版功能允许你只对照片的特定区域进行调整。比如：</p>
<p>选择主体后稍微提亮面部；选择天空后增加饱和度让天空更蓝；用径向渐变在面部周围做一个柔和的暗角效果。</p>
<h2>预设的使用与创建</h2>
<p>预设（Preset）是一键式滤镜，可以快速为照片套用特定的风格。你可以下载其他摄影师分享的预设，也可以将自己常用的调整保存为预设，大大提升修图效率。</p>`,
    category: 'editing',
    coverImage: 'https://readdy.ai/api/search-image?query=Mobile%20phone%20displaying%20Adobe%20Lightroom%20editing%20interface%20with%20a%20portrait%20photo%20being%20edited%2C%20minimalist%20desk%20setup%2C%20warm%20ambient%20lighting%2C%20clean%20cream%20background%2C%20modern%20digital%20workflow%20concept&width=800&height=500&seq=article-edit-01&orientation=landscape',
    author: '后期达人',
    date: '2026-07-10',
    readTime: '12分钟',
    tags: ['Lightroom', '手机修图', '后期入门', '调色教程'],
    featured: true,
  },
  {
    id: '8',
    slug: 'editing-skin-retouch',
    title: '人像精修：皮肤质感的自然处理技巧',
    excerpt: '自拍修图的最大难点在于如何让皮肤看起来光滑自然而不失真。掌握这些专业级的皮肤处理技巧，告别"塑料脸"。',
    content: `<p>"修得像假人"是自拍修图中最常见的翻车现场。好的皮肤处理应该是让人看不出修过的痕迹——皮肤光滑了，但毛孔、纹理这些真实质感依然保留。</p>
<h2>适度原则</h2>
<p>皮肤处理的黄金法则是：宁少勿多。保留一些自然的皮肤纹理，反而比完美无瑕的皮肤看起来更真实、更有质感。记住，真正的皮肤有毛孔、有细微的纹理、有光影变化。</p>
<h2>Lightroom中的皮肤处理</h2>
<p>1. 降低清晰度（-10到-20）：让皮肤纹理柔和一些。</p>
<p>2. 适度降低纹理（-15到-25）：进一步柔化但不完全抹去质感。</p>
<p>3. 用蒙版只对皮肤区域应用这些调整，避免影响眼睛、头发等需要清晰度的部分。</p>
<h2>局部瑕疵处理</h2>
<p>对于痘痘、斑点等局部瑕疵，使用修复画笔工具进行点状修复。每处瑕疵单独处理，不要大面积涂抹，否则皮肤会失去自然的光影变化。</p>
<h2>色彩统一</h2>
<p>面部不同区域往往有不同的色调：额头偏黄、脸颊偏红、下巴偏青。利用HSL工具针对性地调整这些区域的色彩，让面部肤色更加均匀自然。</p>`,
    category: 'editing',
    coverImage: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20retouching%20before%20and%20after%20comparison%20on%20a%20digital%20screen%2C%20showing%20natural%20skin%20texture%20preservation%2C%20minimalist%20workspace%2C%20warm%20lighting%2C%20cream%20background%2C%20clean%20editorial%20style&width=800&height=500&seq=article-edit-02&orientation=landscape',
    author: '后期达人',
    date: '2026-07-08',
    readTime: '10分钟',
    tags: ['人像精修', '皮肤处理', '自然质感', '后期技巧'],
    featured: false,
  },
  {
    id: '9',
    slug: 'editing-color-grading',
    title: '调色思路分享：打造电影感自拍的色彩秘诀',
    excerpt: '色彩是摄影作品中最重要的情感表达元素。学习电影级调色思路，用色彩讲述你的自拍故事。',
    content: `<p>你有没有发现，电影中的每一帧画面都有统一的色调和氛围？这就是调色的魔力——通过精心设计的色彩方案来传达情感和故事。你的自拍也可以拥有这种电影感。</p>
<h2>确定色调方向</h2>
<p>在开始调色之前，先想清楚你想表达什么情绪：</p>
<p>温暖治愈：橙黄色调为主，配合柔和的阴影。</p>
<p>清冷高级：蓝绿色调为主，降低饱和度。</p>
<p>复古胶片：略微偏黄绿，增加颗粒感和暗角。</p>
<p>日系清新：高光偏青，阴影偏暖，整体偏亮。</p>
<h2>HSL工具的深度运用</h2>
<p>HSL（色相、饱和度、明度）是精细化调色的核心工具。你可以单独调整画面中某一类颜色的表现。例如，想让天空更青、肤色更暖、绿叶更沉，都可以通过HSL来实现。</p>
<h2>色调分离</h2>
<p>这是打造电影感的终极武器。简单来说，就是给高光部分加一种色调（比如暖金色），给阴影部分加另一种色调（比如冷青色）。这种冷暖对比能创造出极强的视觉张力。</p>
<h2>实践：一分钟调出电影感</h2>
<p>1. 降低整体饱和度10-15%。</p>
<p>2. 色调分离：高光加暖橙色，阴影加青蓝色。</p>
<p>3. 增加一点点暗角。</p>
<p>4. 微调曲线，将黑色部分略微提亮，营造柔和的胶片感。</p>`,
    category: 'editing',
    coverImage: 'https://readdy.ai/api/search-image?query=Cinematic%20color%20grading%20palette%20displayed%20on%20a%20digital%20screen%20with%20warm%20orange%20and%20teal%20tones%2C%20film%20color%20grading%20concept%2C%20minimalist%20workspace%2C%20soft%20warm%20lighting%2C%20cream%20background%2C%20professional%20creative%20atmosphere&width=800&height=500&seq=article-edit-03&orientation=landscape',
    author: '调色艺术家',
    date: '2026-07-05',
    readTime: '9分钟',
    tags: ['调色思路', '电影感', '色彩理论', 'HSL工具'],
    featured: true,
  },
  {
    id: '10',
    slug: 'sharing-my-journey',
    title: '我的自拍进化史：从新手小白到社区达人',
    excerpt: '一位普通摄影爱好者的自拍成长故事。从第一张令人失望的自拍到获得社区认可，这一路走来收获的不只是技术。',
    content: `<p>三年前，我和大多数人一样，拿起手机自拍只是简单地按一下快门。照片要么过曝、要么模糊、要么构图奇怪。我从未想过，自拍也可以是一门艺术。</p>
<h2>起点：认清自己的不足</h2>
<p>我的第一张"认真"自拍是在一个阳光明媚的下午。我站在窗前，学着网上的教程打开了网格线，试着把人放在三分线的交叉点上。结果呢？脸是歪的，光线把鼻子照得特别大，背景乱成一团。但我没有灰心，因为我知道每个人都有这样的起点。</p>
<h2>转折：发现光线的重要性</h2>
<p>真正的转折发生在一个黄昏。我偶然在日落时分拍了一张逆光自拍，金色的阳光从身后穿过发丝，形成了一圈梦幻的光晕。那一刻我明白了——光线才是摄影的灵魂。从那以后，我开始刻意选择拍摄时间，研究不同光线条件下的效果。</p>
<h2>成长：从模仿到创造</h2>
<p>我花了一年时间模仿我喜欢的摄影师的风格和构图。模仿是最好的学习方式。渐渐地，我开始有了自己的审美偏好——我喜欢温暖柔和的色调，喜欢利用环境元素做框架构图，喜欢在平凡的场景中寻找不平凡的角度。</p>
<h2>现在：与社区一起成长</h2>
<p>如今，我的自拍作品开始在社区中被认可。但我深知，摄影是一场没有终点的旅程。每一位给我点赞和评论的朋友，都是我继续前进的动力。希望我的故事能给正在路上的你一些启发。</p>`,
    category: 'sharing',
    coverImage: 'https://readdy.ai/api/search-image?query=Inspiring%20photography%20journey%20visual%20story%20showing%20progression%20from%20beginner%20to%20skilled%20photographer%2C%20warm%20nostalgic%20aesthetic%2C%20cream%20and%20golden%20tones%2C%20creative%20lifestyle%20concept%2C%20minimalist%20studio%20with%20photos%20displayed&width=800&height=500&seq=article-share-01&orientation=landscape',
    author: '小鹿同学',
    date: '2026-07-02',
    readTime: '10分钟',
    tags: ['自拍故事', '成长经历', '达人分享', '摄影心得'],
    featured: false,
  },
  {
    id: '11',
    slug: 'sharing-travel-selfie',
    title: '旅行自拍技巧大公开：一个人的旅行也能出大片',
    excerpt: '独自旅行时如何拍出高质量的自拍？从设备选择到取景技巧，这篇旅行自拍攻略覆盖所有你需要知道的内容。',
    content: `<p>独自旅行最大的遗憾是什么？当然是没有人帮你拍照！但掌握了旅行自拍技巧，一个人的旅行同样可以出大片。甚至，因为不需要迁就别人的时间，你可以更自由地等待最佳光线和角度。</p>
<h2>必备装备</h2>
<p>1. 便携三脚架：选择可以折叠到手机大小的迷你三脚架，方便随身携带。</p>
<p>2. 蓝牙遥控器：几块钱的小配件，却是旅行自拍的神器。</p>
<p>3. 手机广角附加镜：在狭小空间或需要收纳更多背景时非常实用。</p>
<h2>取景与构图</h2>
<p>旅行自拍的取景原则：让环境和人物各占50%的画面比重。既要让人看清你的表情和穿搭，也要让观众感受到旅行地的独特氛围。避免人物占满整个画面，那样跟在家自拍有什么区别呢？</p>
<h2>利用定时连拍</h2>
<p>大多数手机都有定时连拍功能。设置3秒或10秒延时，然后连拍10张。这样你可以在镜头前自然地走动、转身、变换姿势，最后从连拍中选出最自然的一张。</p>
<h2>最佳拍摄时间</h2>
<p>旅行中最美的光线往往出现在清晨和傍晚。这不仅是因为黄金时刻的光线美，更重要的是这两个时间段的游客相对较少，你可以更从容地取景构图。</p>`,
    category: 'sharing',
    coverImage: 'https://readdy.ai/api/search-image?query=Solo%20traveler%20taking%20a%20selfie%20with%20a%20tripod%20in%20a%20beautiful%20scenic%20location%2C%20warm%20golden%20hour%20light%2C%20adventure%20and%20freedom%20vibe%2C%20cream%20and%20warm%20tones%2C%20travel%20photography%20aesthetic&width=800&height=500&seq=article-share-02&orientation=landscape',
    author: '旅行摄影师',
    date: '2026-06-28',
    readTime: '9分钟',
    tags: ['旅行自拍', '三脚架', '连拍技巧', '取景构图'],
    featured: false,
  },
  {
    id: '12',
    slug: 'equipment-phone-accessories',
    title: '手机摄影必备配件推荐：从入门到进阶',
    excerpt: '好的工具能让你的自拍事半功倍。盘点从入门到进阶的手机摄影配件，帮你花最少的钱获得最大的提升。',
    content: `<p>手机摄影最大的优势是便捷，但适当的配件可以弥补手机硬件的不足，让你的自拍作品质量实现质的飞跃。以下是经过实测筛选的推荐清单。</p>
<h2>入门级：百元以内搞定基础三件套</h2>
<p>1. 迷你三脚架（约30元）：选择带手机夹和球型云台的款式，可以灵活调整角度。</p>
<p>2. 蓝牙遥控快门（约10元）：比定时拍摄更方便，可以随时按下快门。</p>
<p>3. 手机广角镜头夹（约50元）：扩大取景范围，适合旅行和多人自拍。</p>
<h2>进阶级：让手机变身专业设备</h2>
<p>1. 便携补光灯（约100元）：带色温调节的LED补光灯，夜间自拍利器。</p>
<p>2. 手机兔笼（约150元）：一个框架结构，可以同时安装补光灯、麦克风等多个配件。</p>
<p>3. ND滤镜（约80元）：在强光下实现慢速快门效果，创造动感自拍。</p>
<h2>配件的正确使用方式</h2>
<p>买了配件不等于就能拍出好照片。三脚架要配合构图的练习才有意义，补光灯需要理解光线方向才能用好。配件是工具，技巧才是核心。</p>
<h2>不推荐的配件</h2>
<p>那些标榜"60倍变焦"的手机望远镜镜头夹，大部分画质极差，不建议购买。另外，太过便宜的自拍杆往往稳定性差，容易抖动导致照片模糊。</p>`,
    category: 'equipment',
    coverImage: 'https://readdy.ai/api/search-image?query=Phone%20photography%20accessories%20beautifully%20arranged%20on%20a%20minimalist%20wooden%20surface%2C%20including%20tripod%20lens%20attachments%20and%20ring%20light%2C%20warm%20natural%20lighting%2C%20cream%20background%2C%20product%20flat%20lay%20photography&width=800&height=500&seq=article-equip-01&orientation=landscape',
    author: '器材评测师',
    date: '2026-06-25',
    readTime: '11分钟',
    tags: ['手机配件', '器材推荐', '三脚架', '补光灯'],
    featured: false,
  },
];